import {http} from '../http'
import * as coreSessions from '../../coreApi/sessions'
import * as coreDivisions from '../../coreApi/divisions'
import * as coreBunks from '../../coreApi/bunks'
import * as corePersons from '../../coreApi/persons'
import {APIResponse} from '../types'
import {handleError} from '../handleError'
import {ReportCamper} from '../../../types'
import {RawSessionAttendee} from '../../coreApi/sessions/attendees'
import {RawDivisionAttendee} from '../../coreApi/divisions/attendees'
import {RawBunkAssignments} from '../../coreApi/bunks/assignments'

interface Params {
  clientId: number
  season: number
  sessionIds: number[]
  divisionIds: number[]
  bunkPlanId?: number
  /**
   * The selected bunk plan's own bunk ids. Required alongside `bunkPlanId`
   * because the bunks service declares BOTH `bunkplanids` and `bunkids` on
   * `/assignments` — a plan id alone is not a valid request. The bunk-plans list
   * the filter is built from already carries them, so this costs no extra call.
   */
  bunkIds?: number[]
}

export interface ServiceMethod {
  (params: Params): Promise<APIResponse<ReportCamper[]>>
}

const ERROR_PATH = 'api.roster.fetchReportRoster()'
const GENERIC_ERROR =
  'The report could not be produced — the camper roster could not be retrieved. Please try again.'

const PAGE_SIZE = 500
// See the note on MAX_PAGES in api/faces/getTaggingCoverage: bounded so a
// non-terminating `Next` cannot spin the tab, and set far above any real roster.
const MAX_PAGES = 200

// Attendee status flag for Enrolled. AC-6 is about every camper ENROLLED in the
// current season, so applicants, wait-lists and cancellations are not the
// report's subject.
const STATUS_ENROLLED = 2

// One `id=` query param per person, so the batch size bounds URL length.
// Mirrors the batching in api/persons/getNames.
const PERSONS_BATCH_SIZE = 100

// A camper with no division assignment comes back as DivisionID 0 rather than
// being omitted. 0 is "unassigned", not a division.
const UNASSIGNED_DIVISION_ID = 0

/**
 * Read every page of a paged CampMinder collection, following `Next`.
 *
 * Returns the whole set or throws — never a partial one. A truncated roster is
 * indistinguishable from a small camp to everything downstream, and AC-15 is
 * explicit that a roster that cannot be retrieved must not print at all.
 */
const readAllPages = async <T>(
  fetchPage: (pageNumber: number) => Promise<{
    data: {TotalCount: number; Next: string; Results: T[]}
  }>,
): Promise<T[]> => {
  const items: T[] = []
  let page = 1

  while (page <= MAX_PAGES) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchPage(page)
    if (!response || !response.data) {
      throw new Error('Unsuccessful paged read')
    }

    const {Results, Next} = response.data
    ;(Results ?? []).forEach((r) => items.push(r))

    if (!Next) return items
    page += 1
  }

  throw new Error(`Paged read exceeded the ${MAX_PAGES}-page cap`)
}

const sessionIdsFor = (attendee: RawSessionAttendee): number[] =>
  Array.from(
    new Set((attendee.SessionProgramStatus ?? []).map((s) => s.SessionID)),
  ).sort((a, b) => a - b)

/**
 * The camper set for the report, composed from enrollment facts.
 *
 * D-H: the driving reads are the session and division ATTENDEE endpoints, never
 * the messaging-audience source. That source filters to people with a linked
 * guardian, and a camper with an incomplete record is disproportionately likely
 * to be both guardian-less and untrained — so it hides exactly the campers this
 * report exists to surface, while looking correct in any clean test camp
 * (AC-6).
 *
 * Names come back as first/last separately rather than as one display string,
 * because the report is ordered by surname then given name (AC-7) and a
 * concatenated name cannot be sorted that way.
 */
export const fetchReportRoster: ServiceMethod = async ({
  clientId,
  season,
  sessionIds,
  divisionIds,
  bunkPlanId,
  bunkIds,
}) => {
  try {
    // Sessions always drive the base set: with no session filter this is every
    // enrolled camper in the season, which is the AC-6 population.
    const sessionAttendees = await readAllPages<RawSessionAttendee>((page) =>
      coreSessions.listSessionAttendees(http, {
        ClientID: clientId,
        SeasonID: season,
        SessionIDs: sessionIds,
        Status: STATUS_ENROLLED,
        PageNumber: page,
        PageSize: PAGE_SIZE,
      }),
    )

    const sessionsByPerson = new Map<number, number[]>()
    sessionAttendees.forEach((a) => {
      const existing = sessionsByPerson.get(a.PersonID) ?? []
      sessionIdsFor(a).forEach((id) => {
        if (!existing.includes(id)) existing.push(id)
      })
      sessionsByPerson.set(a.PersonID, existing)
    })

    // Divisions are read whether or not they are filtered on: the report shows a
    // division column either way, so this read is both the filter and the label
    // source.
    const divisionAttendees = await readAllPages<RawDivisionAttendee>((page) =>
      coreDivisions.listDivisionAttendees(http, {
        ClientID: clientId,
        SeasonID: season,
        DivisionIDs: divisionIds,
        PageNumber: page,
        PageSize: PAGE_SIZE,
      }),
    )

    const divisionsByPerson = new Map<number, number[]>()
    divisionAttendees.forEach((a) => {
      if (a.DivisionID === UNASSIGNED_DIVISION_ID) return
      const existing = divisionsByPerson.get(a.PersonID) ?? []
      if (!existing.includes(a.DivisionID)) existing.push(a.DivisionID)
      divisionsByPerson.set(a.PersonID, existing)
    })

    // Intersect only when divisions are actually filtered on. Intersecting
    // unconditionally would silently drop every camper with no division
    // assignment — the same class of quiet omission D-H rejects.
    const personIds = Array.from(sessionsByPerson.keys()).filter((personId) =>
      divisionIds.length === 0 ? true : divisionsByPerson.has(personId),
    )

    if (personIds.length === 0) {
      return {result: 'success', data: []}
    }

    // Per-camper bunk, only when a bunk plan is selected (AC-8).
    const bunkByPerson = new Map<number, number>()
    if (bunkPlanId !== undefined && (bunkIds ?? []).length > 0) {
      const bunkRows = await readAllPages<RawBunkAssignments>((page) =>
        coreBunks.listBunkAssignments(http, {
          ClientID: clientId,
          SeasonID: season,
          BunkPlanIDs: [bunkPlanId],
          BunkIDs: bunkIds ?? [],
          PageNumber: page,
          PageSize: PAGE_SIZE,
        }),
      )

      bunkRows.forEach((row) => {
        ;(row.Assignments ?? []).forEach((assignment) => {
          // A deleted assignment is history, not a current bunk. Printing it
          // would put a camper in a bunk they are not in.
          if (assignment.IsDeleted) return
          bunkByPerson.set(assignment.PersonID, row.BunkID)
        })
      })
    }

    // Names, batched so an unbounded id list cannot blow the URL length.
    const batches: number[][] = []
    for (let i = 0; i < personIds.length; i += PERSONS_BATCH_SIZE) {
      batches.push(personIds.slice(i, i + PERSONS_BATCH_SIZE))
    }

    const personResponses = await Promise.all(
      batches.map((batch) =>
        corePersons.list(http, {
          ClientID: clientId,
          IDs: batch,
          PageNumber: 1,
          PageSize: PERSONS_BATCH_SIZE,
        }),
      ),
    )

    const namesByPerson = new Map<number, {first: string; last: string}>()
    personResponses.forEach((response) => {
      ;(response?.data?.Results ?? []).forEach((raw) => {
        namesByPerson.set(Number(raw.ID), {
          first: raw.Name?.First ?? '',
          last: raw.Name?.Last ?? '',
        })
      })
    })

    const campers: ReportCamper[] = personIds.map((personId) => {
      const name = namesByPerson.get(personId)
      return {
        personId,
        // A name the persons service didn't return must not remove the camper
        // from the report — an unnamed row still carries a coverage answer, and
        // dropping it would understate the untrained population.
        firstName: name?.first ?? '',
        lastName: name?.last ?? '',
        sessionIds: sessionsByPerson.get(personId) ?? [],
        divisionIds: divisionsByPerson.get(personId) ?? [],
        bunkId: bunkByPerson.get(personId) ?? null,
      }
    })

    return {result: 'success', data: campers}
  } catch (e) {
    return handleError(ERROR_PATH, GENERIC_ERROR, e)
  }
}
